import { IMicrositeRecipientForList } from '@nkr/models/microsites/microsite-recipient-for-list';
import { useEffect, useState } from 'react';
import { Modal, Button, FormGroup, ControlLabel, FormControl } from 'react-bootstrap';
import { Typeahead } from 'react-bootstrap-typeahead';


interface IMicrositePatientOption extends IMicrositeRecipientForList {
    label: string;
}

interface IProps {
    show: boolean;
    donorName: string;
    patientList: IMicrositeRecipientForList[];
    onHide: () => void;
    onConfirm: (micrositeRecipientId: number) => void;
}

type TStep = 'select' | 'confirm';

interface IState {
    step: TStep;
    selected: IMicrositePatientOption[];
}

const initialState: IState = {
    step: 'select',
    selected: []
};



const IntendedMicrositeRecipientModal = (props: IProps) => {
    const [state, setState] = useState<IState>(initialState);

    useEffect(() => {
        if (props.show) {
            setState(initialState);
        }
    }, [props.show]);

    const patientOptions: IMicrositePatientOption[] = Array.isArray(props.patientList)
        ? props.patientList.map(p => ({
            ...p,
            label: p.firstName + ' ' + p.lastName
        }))
        : [];

    const handleSelectionChange = (selected: IMicrositePatientOption[]) => {
        setState(prev => ({ ...prev, selected }));
    };

    const handleNext = () => {
        if (state.selected.length > 0) {
            setState(prev => ({ ...prev, step: 'confirm' }));
        }
    };

    const handleBack = () => {
        setState(prev => ({ ...prev, step: 'select' }));
    };

    const handleConfirm = () => {
        if (state.selected.length > 0) {
            const { id, } = state.selected[0];
            props.onConfirm(id);
        }
    };

    const selectedPatient = state.selected.length > 0 ? state.selected[0] : null;
    return (
        <Modal show={props.show} onHide={props.onHide} backdrop="static" keyboard={false}>
            <Modal.Header closeButton>
                <Modal.Title>
                    {state.step == 'select' ? 'Add Intended Microsite Recipient' : 'Confirm Intended Recipient'}
                </Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <div style={{
                    position: 'relative',
                    transition: 'min-height 0.3s ease',
                    minHeight: 200
                }}>
                    {/* Step 1 */}
                    <div style={{
                        transition: 'opacity 0.25s ease, transform 0.25s ease',
                        opacity: state.step == 'select' ? 1 : 0,
                        transform: state.step == 'select' ? 'translateX(0)' : 'translateX(-30px)',
                        pointerEvents: state.step == 'select' ? 'auto' : 'none',
                        position: state.step == 'select' ? 'relative' : 'absolute',
                        top: 0, left: 0, right: 0
                    }}>
                        <p>
                            Please enter the name of the Microsite patient you would like to add as the intended recipient for: <strong>{props.donorName}</strong>
                        </p>

                        <FormGroup>
                            <Typeahead
                                id='microsite-patient-typeahead'
                                clearButton
                                labelKey='label'
                                options={patientOptions}
                                selected={state.selected}
                                filterBy={['firstName', 'lastName', 'shortCode']}
                                placeholder='Type Microsite Patient Name'
                                renderInput={(inputProps) => (
                                    <FormControl {...inputProps} />
                                )}
                                renderMenuItemChildren={(option: IMicrositePatientOption) => (
                                    <div style={{ borderBottom: '1px solid #ddd', paddingBottom: 5 }}>
                                        <div>Microsite (Short Code) ID: {option.shortCode}</div>
                                        {option.firstName != '' && option.lastName != '' && (
                                            <div><small>Name: {option.firstName} {option.lastName}</small></div>
                                        )}
                                        <div><small>DOB: {option.birthdate}</small></div>
                                    </div>
                                )}
                                onChange={handleSelectionChange}
                            />
                        </FormGroup>

                        <div style={{ marginTop: 20, textAlign: 'right' }}>
                            <Button
                                bsStyle="primary"
                                disabled={state.selected.length == 0}
                                onClick={handleNext}
                                style={{ marginRight: 10 }}
                            >
                                Continue
                            </Button>
                            <Button onClick={props.onHide}>Cancel</Button>
                        </div>
                    </div>

                    {/* Step 2 */}
                    <div style={{
                        transition: 'opacity 0.25s ease, transform 0.25s ease',
                        opacity: state.step == 'confirm' ? 1 : 0,
                        transform: state.step == 'confirm' ? 'translateX(0)' : 'translateX(30px)',
                        pointerEvents: state.step == 'confirm' ? 'auto' : 'none',
                        position: state.step == 'confirm' ? 'relative' : 'absolute',
                        top: 0, left: 0, right: 0
                    }}>
                        <p>
                            <strong>({selectedPatient?.firstName} {selectedPatient?.lastName})</strong> will be added as the intended recipient for <strong>({props.donorName})</strong> and the donor will be converted to a Standard Voucher donor.
                        </p>
                        <p>If this is correct, please click confirm below. If incorrect, select cancel.</p>

                        <div style={{ marginTop: 20, textAlign: 'right' }}>
                            <Button
                                bsStyle="success"
                                onClick={handleConfirm}
                                style={{ marginRight: 10 }}>
                                Confirm
                            </Button>
                            <Button bsStyle="primary"
                                onClick={handleBack}
                                style={{ marginRight: 10 }}>
                                Back
                            </Button>
                            <Button bsStyle="primary" onClick={props.onHide}>Cancel</Button>
                        </div>
                    </div>
                </div>
            </Modal.Body>
        </Modal>
    );
};

export default IntendedMicrositeRecipientModal;